import { supabase } from './supabase';

// Generate a cryptographically secure code verifier
const generateCodeVerifier = (length = 128) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => 
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'[byte % 66]
  ).join('');
};

// Base64-URL encode an ArrayBuffer
const base64UrlEncode = (buffer: ArrayBuffer): string => {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

// Generate PKCE code challenge from verifier
const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  try {
    // Convert the code verifier to UTF-8 bytes
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    
    // Generate SHA-256 hash of the code verifier
    const digest = await crypto.subtle.digest('SHA-256', data);
    
    // Base64-URL encode the hash to create the code challenge
    return base64UrlEncode(digest);
  } catch (error) {
    console.error('Error generating code challenge:', error);
    throw new Error('Failed to generate PKCE code challenge');
  }
};

// Generate a secure state parameter
const generateState = (): string => {
  try {
    // Generate 32 bytes (256 bits) of random data for the state
    const stateArray = new Uint8Array(32);
    crypto.getRandomValues(stateArray);
    
    // Convert to Base64URL format
    const state = base64UrlEncode(stateArray.buffer);
    
    // Verify the state is not empty
    if (!state) {
      throw new Error('Generated state is empty');
    }
    
    return state;
  } catch (error) {
    console.error('Error generating state parameter:', error);
    throw new Error('Failed to generate secure state parameter');
  }
};

// Start YouTube OAuth flow
export const startYoutubeOAuth = async () => {
  try {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      throw new Error('Not authenticated');
    }
    
    // Store user ID in cookie
    document.cookie = `user_id=${user.id}; path=/; secure; samesite=lax; max-age=3600`;
    
    // Generate and store PKCE code verifier
    const codeVerifier = generateCodeVerifier();
    document.cookie = `code_verifier=${codeVerifier}; path=/; secure; samesite=lax; max-age=3600`;
    
    // Generate state parameter
    const state = generateState();
    document.cookie = `oauth_state=${state}; path=/; secure; samesite=lax; max-age=3600`;

    // Generate code challenge
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    
    // Get client ID from environment variables
    const clientId = import.meta.env.VITE_YOUTUBE_CLIENT_ID;
    if (!clientId) {
      throw new Error('Please configure the YouTube client ID in your environment variables');
    }
    
    // Construct callback URL
    const redirectUri = 'https://serge.unleashsupport.com/.netlify/functions/youtubeCallback';
    
    // Define required scopes
    const scope = [
      'https://www.googleapis.com/auth/youtube.readonly',
      'https://www.googleapis.com/auth/youtube.upload'
    ].join(' ');
    
    // Construct Google's OAuth 2.0 authorization URL
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    
    // Add all required OAuth 2.0 parameters
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('client_id', clientId);
    authUrl.searchParams.append('redirect_uri', redirectUri);
    authUrl.searchParams.append('scope', scope);
    authUrl.searchParams.append('state', state);
    authUrl.searchParams.append('code_challenge', codeChallenge);
    authUrl.searchParams.append('code_challenge_method', 'S256');
    authUrl.searchParams.append('access_type', 'offline');
    authUrl.searchParams.append('prompt', 'consent');
    
    // Redirect to Google's authorization page
    window.location.href = authUrl.toString();
  } catch (err) {
    console.error('Error initiating YouTube OAuth flow:', err);
    throw err;
  }
};